import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CloudUpload } from 'lucide-react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Spinner } from '@/components/ui/Spinner';
import { mediaService } from '@/services/mediaService';
import type { MediaItem } from '@/types/blog';

interface MediaPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (media: { url: string; alt_text?: string }) => void;
}

export function MediaPicker({ isOpen, onClose, onSelect }: MediaPickerProps) {
  const [tab, setTab] = useState('library');
  const [items, setItems] = useState<MediaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<MediaItem | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const result = await mediaService.list({
        search: search || undefined,
        page,
        pageSize: 24,
      });
      setItems(result.data);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load media:', err);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    if (isOpen && tab === 'library') {
      loadMedia();
    }
  }, [isOpen, tab, loadMedia]);

  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      setTab('library');
      setPage(1);
      setSearch('');
    }
  }, [isOpen]);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: MediaItem[] = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        const item = await mediaService.uploadFile(file);
        uploaded.push(item);
      }
      if (uploaded.length > 0) {
        // Auto-select the first uploaded item
        setSelected(uploaded[0]);
        setTab('library');
        loadMedia();
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleSelect = () => {
    if (selected) {
      onSelect({ url: selected.url, alt_text: selected.alt_text || undefined });
      onClose();
    }
  };

  const totalPages = Math.ceil(total / 24);

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Media Library"
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSelect}
            disabled={!selected}
          >
            Select Image
          </Button>
        </>
      }
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="library">Media Library</TabsTrigger>
          <TabsTrigger value="upload">Upload New</TabsTrigger>
        </TabsList>

        <TabsContent value="library">
          <div className="mb-4">
            <Input
              placeholder="Search images..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No images found.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setTab('upload')}
              >
                Upload an image
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 max-h-[400px] overflow-y-auto">
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelected(item)}
                    className={`relative aspect-square rounded-md overflow-hidden border-2 transition-all hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring ${
                      selected?.id === item.id
                        ? 'border-primary ring-2 ring-primary/30'
                        : 'border-transparent'
                    }`}
                  >
                    <img
                      src={item.url}
                      alt={item.alt_text || item.original_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {selected?.id === item.id && (
                      <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                        <div className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">
                          ✓
                        </div>
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Selected info */}
              {selected && (
                <div className="mt-3 p-3 bg-muted rounded-md text-sm flex items-center gap-3">
                  <img
                    src={selected.url}
                    alt=""
                    className="w-10 h-10 object-cover rounded"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground truncate">{selected.original_name}</p>
                    <p className="text-muted-foreground">
                      {selected.width && selected.height ? `${selected.width}×${selected.height}` : ''}
                      {selected.file_size ? ` · ${formatFileSize(selected.file_size)}` : ''}
                    </p>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="upload">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragOver ? 'border-primary bg-primary/5' : 'border-border'
            }`}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Spinner size="lg" />
                <p className="text-sm text-muted-foreground">Uploading...</p>
              </div>
            ) : (
              <>
                <CloudUpload className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <p className="mt-3 text-sm text-muted-foreground">
                  Drag and drop images here, or
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Browse Files
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files)}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  PNG, JPG, GIF, WebP up to 25MB
                </p>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </Modal>
  );
}
